import {getTestProps} from '@benchling/js-test-utils';
import {Box, Button, createStyles, Group, Space, Stack, Title} from '@mantine/core';
import React, {useCallback, useEffect, useRef, useState} from 'react';

import {trackEvent} from 'analytics/Analytics';
import {getLocationPathname} from 'BenchlingUtil';
import EntryCalendar from 'calendar/EntryCalendar';
import {HOMEPAGE_ANALYTICS_ACTIONS} from 'constants/analytics-products/HomepageAnalyticsConstants';
import {useRolloutFlags} from 'launchdarkly/LaunchDarklyUtil';
import {getActionButtonRoleProps} from 'lib/a11y/ButtonA11y';
import * as SidepanelActions from 'nav/sidepanel/SidepanelActions';
import EntityStore from 'stores/EntityStore';
import useOnMount from 'ui-library/hooks/useOnMount';
import useStoreSelector from 'ui-library/hooks/useStoreSelector';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import ActivitySection from './homepage/ActivitySection';
import AISection from './homepage/AISection';
import {
  COLUMN_SPACING,
  HOMEPAGE_PATH,
  MAX_CONTENT_WIDTH,
  SINGLE_COLUMN_MODE_MAX_WIDTH,
} from './homepage/HomepageConstants';
import HomepagePreviewBanner from './homepage/HomepagePreviewBanner';
import {
  shouldShowAISection,
  shouldShowMyWorkSection,
  shouldShowWelcomeSection,
  shouldShowWorklistsSection,
} from './homepage/HomepageVisibility';
import MyWorkSection from './homepage/MyWorkSection';
import OrganizationInviteBanner from './homepage/OrganizationInviteBanner';
import RecentsSection from './homepage/RecentsSection';
import {useIsSmallerThan} from './homepage/useIsSmallerThan';
import WelcomeSection from './homepage/WelcomeSection';
import WorklistsSection from './homepage/WorklistsSection';

const useCardsStyles = createStyles((theme) => ({
  columnContainer: {
    maxWidth: MAX_CONTENT_WIDTH,
    margin: '0 auto',
  },
  columnContainerWrapper: {
    flexGrow: 1,
    padding: `${theme.spacing.lg} ${COLUMN_SPACING}`,
    backgroundColor: theme.colors.gray[0],
  },
  column: {
    width: `calc(50% - ${COLUMN_SPACING} / 2)`,
  },
}));

const useHomepageStyles = createStyles((theme, {showHeaderShadow}: {showHeaderShadow: boolean}) => ({
  closeIcon: {
    color: theme.colors.gray[6],
    cursor: 'pointer',
    alignSelf: 'flex-start',
    '&&': {
      height: 18,
      width: 18,
    },
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
  },
  negativeXsMarginLeft: {
    marginLeft: `-${theme.spacing.xs}`,
  },
  xsMarginRight: {
    marginRight: theme.spacing.sm,
  },
  stickyHeader: {
    padding: theme.spacing.xxl,
    backgroundColor: theme.white,
    borderBottom: `2px solid ${theme.colors.gray[0]}`,
    position: 'sticky',
    top: 0,
    zIndex: 1000,
    transition: 'box-shadow 0.25s ease-in-out',
    boxShadow: showHeaderShadow ? '0 2px 12px rgba(0, 0, 0, 0.1)' : 'none',
  },
}));

const HomepageCards = (): JSX.Element => {
  const {isSmallerThan, ref} = useIsSmallerThan(SINGLE_COLUMN_MODE_MAX_WIDTH);
  const {classes} = useCardsStyles();
  const {navEnableHomepageMyWorkRedesign} = useRolloutFlags();
  const enableMyWorkRedesign = navEnableHomepageMyWorkRedesign === true;
  const showMyWork = shouldShowMyWorkSection();
  const showAI = shouldShowAISection();
  const showWorklists = shouldShowWorklistsSection();
  const showWelcome = shouldShowWelcomeSection();

  if (isSmallerThan) {
    return (
      <div className={classes.columnContainerWrapper} ref={ref}>
        <OrganizationInviteBanner />
        <HomepagePreviewBanner />
        {enableMyWorkRedesign && showMyWork && <MyWorkSection />}
        {showAI && <AISection />}
        <RecentsSection />
        {!enableMyWorkRedesign && showMyWork && <MyWorkSection />}
        {showWorklists && <WorklistsSection />}
        {showWelcome && <WelcomeSection />}
        <ActivitySection />
      </div>
    );
  }

  return (
    <div className={classes.columnContainerWrapper} ref={ref}>
      <Box maw={`${MAX_CONTENT_WIDTH}px`} m="0 auto">
        <OrganizationInviteBanner />
        <HomepagePreviewBanner />
        {enableMyWorkRedesign && showMyWork && (
          <>
            <MyWorkSection />
            <Space h="md" />
          </>
        )}
        {showAI && (
          <>
            <AISection />
            <Space h="md" />
          </>
        )}
        <Group
          position="center"
          spacing={COLUMN_SPACING}
          className={classes.columnContainer}
          align="flex-start"
        >
          <Stack className={classes.column}>
            <RecentsSection />
            {showWorklists && <WorklistsSection />}
            {showWelcome && <WelcomeSection />}
          </Stack>
          <Stack className={classes.column}>
            {!enableMyWorkRedesign && showMyWork && <MyWorkSection />}
            <ActivitySection />
          </Stack>
        </Group>
      </Box>
    </div>
  );
};

/** Preference-gated home sidepanel with recents, worklists, my work, and activity sections. */
export default function Homepage(): JSX.Element {
  const [showCalendar, setShowCalendar] = useState<boolean>(false);

  const user = useStoreSelector(
    EntityStore,
    useCallback((store) => store.currentUser(), [])
  );

  useOnMount(() => {
    // The redesigned homepage only supports fully expanded or closed — never semi-expanded.
    if (HOMEPAGE_PATH === getLocationPathname()) {
      SidepanelActions.expand();
    } else {
      SidepanelActions.close({autoOpenFile: false});
    }
  });

  const [showHeaderShadow, setShowHeaderShadow] = useState(false);
  const scrollableRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollableRef.current == null) {
      return;
    }
    const scrollable = scrollableRef.current;
    const handleScroll = (): void => {
      setShowHeaderShadow(scrollable.scrollTop > 0);
    };
    scrollable.addEventListener('scroll', handleScroll);
    return () => {
      scrollable.removeEventListener('scroll', handleScroll);
    };
  }, [scrollableRef]);

  const {classes, cx} = useHomepageStyles({showHeaderShadow});

  return (
    <div
      ref={scrollableRef}
      className={cx(classes.container, 'sidepanel')}
      {...getTestProps({component: 'Homepage', element: 'root'})}
    >
      <Group position="apart" noWrap={true} align="flex-start" className={classes.stickyHeader}>
        <Group spacing="xl">
          <Title order={3}>Welcome back{user != null ? `, ${user.name}` : ''}</Title>
          <Button
            size="md"
            radius="md"
            // eslint-disable-next-line no-restricted-syntax -- no custom variant matches the design, yet
            variant="light"
            className={classes.negativeXsMarginLeft}
            onClick={() => {
              if (!showCalendar) {
                trackEvent(HOMEPAGE_ANALYTICS_ACTIONS.CLICKED_CALENDAR_BUTTON);
              }
              setShowCalendar(!showCalendar);
            }}
            {...getTestProps({component: 'Homepage', element: 'calendarButton'})}
          >
            {showCalendar ? (
              'Back to home'
            ) : (
              <>
                <BenchlingIcon className={classes.xsMarginRight} icon="calendar" /> My Calendar
              </>
            )}
          </Button>
        </Group>
        <BenchlingIcon
          icon="close"
          altText="Close"
          className={classes.closeIcon}
          {...getActionButtonRoleProps({
            onPress: () => {
              trackEvent(HOMEPAGE_ANALYTICS_ACTIONS.CLICKED_CLOSE_BUTTON);
              SidepanelActions.close();
            },
          })}
          {...getTestProps({component: 'Homepage', element: 'closeButton'})}
        />
      </Group>
      {/* Inexplicably, EntryCalendar has only left and top margin */}
      {showCalendar ? (
        <div className="u-spacing-right" data-amp-mask={true}>
          <EntryCalendar />
        </div>
      ) : (
        <HomepageCards />
      )}
    </div>
  );
}
